import * as React from "react";
import { Box } from "@mui/material";
import AppDatePicker, { parseDateOnly } from "./AppDatePicker";

/* ──────────────────────────────────────────────────────────────────────────
   AppDateRangePicker — a start/end pair built from two AppDatePickers so the
   whole app shares one range experience without the paid MUI X range picker.

     • End date can never be earlier than start date: selecting a start date
       raises the end field's minimum date, and an out-of-order value surfaces
       a clear inline error.
     • Inclusive end-date behaviour is preserved (no ±1 day math).
     • Values are date-only "YYYY-MM-DD" strings — no timezone shift.
   ────────────────────────────────────────────────────────────────────────── */

export interface AppDateRangePickerProps {
  startValue?: string | null;
  endValue?: string | null;
  onChangeStart: (value: string) => void;
  onChangeEnd: (value: string) => void;
  startLabel?: React.ReactNode;
  endLabel?: React.ReactNode;
  minDate?: string | null;
  maxDate?: string | null;
  disablePast?: boolean;
  disableFuture?: boolean;
  required?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  format?: string;
  /** Layout of the two fields. */
  direction?: "row" | "column";
  gap?: number;
  /** Override the default "End date cannot be earlier than start date" text. */
  invalidRangeMessage?: string;
  sx?: any;
}

const AppDateRangePicker: React.FC<AppDateRangePickerProps> = ({
  startValue,
  endValue,
  onChangeStart,
  onChangeEnd,
  startLabel = "Start date",
  endLabel = "End date",
  minDate,
  maxDate,
  disablePast = false,
  disableFuture = false,
  required = false,
  disabled = false,
  clearable = true,
  size = "small",
  fullWidth = true,
  format,
  direction = "row",
  gap = 2,
  invalidRangeMessage = "End date cannot be earlier than start date",
  sx,
}) => {
  const start = parseDateOnly(startValue);
  const end = parseDateOnly(endValue);
  // Inclusive comparison — an end equal to start is valid.
  const rangeInvalid = !!start && !!end && end.isBefore(start, "day");

  return (
    <Box sx={{ display: "flex", flexDirection: direction, gap, ...(sx || {}) }}>
      <AppDatePicker
        label={startLabel}
        value={startValue}
        onChange={onChangeStart}
        minDate={minDate}
        maxDate={endValue || maxDate}
        disablePast={disablePast}
        disableFuture={disableFuture}
        required={required}
        disabled={disabled}
        clearable={clearable}
        size={size}
        fullWidth={fullWidth}
        format={format}
      />
      <AppDatePicker
        label={endLabel}
        value={endValue}
        onChange={onChangeEnd}
        // Selecting a start date raises the minimum selectable end date.
        minDate={startValue || minDate}
        maxDate={maxDate}
        disablePast={disablePast}
        disableFuture={disableFuture}
        required={required}
        disabled={disabled}
        clearable={clearable}
        size={size}
        fullWidth={fullWidth}
        format={format}
        error={rangeInvalid}
        helperText={rangeInvalid ? invalidRangeMessage : undefined}
      />
    </Box>
  );
};

export default AppDateRangePicker;
